import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Card, CardContent, Button, useTheme, Fab, Tooltip, Grid } from '@mui/material';
import { subDays } from 'date-fns';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncHighlight from './components/SyncHighlight';
import DemandFilterPopover from './components/filtersComponents/DemandFilterPopover';
import CartFilterPopover from './components/filtersComponents/CartFilterPopover';
import TripFilterPopover from './components/filtersComponents/TripFilterPopover';
import CartSummaryStats from './components/CartSummaryStats';
import SettingsDialog from './components/settingsComponents/SettingsDialog';
import useStatisticsConfig from './hooks/useStatisticsConfig';
import useStatisticsData from './hooks/useStatisticsData';
import useStatisticsFilters from './hooks/useStatisticsFilters';
import usePopoverStates from './hooks/usePopoverStates';
import { userTypeOptions, creationPeriodOptions, cartTypeOptions, tripStatusOptions, tripTypeOptions } from './constants/filterOptions';
import useAuth from "../../../hooks/Auth/useAuth";
const Statistics_new = () => {
  const theme = useTheme();
  const today = new Date();
  
  // Custom hooks for configuration and data management
  const config = useStatisticsConfig();
  const data = useStatisticsData();
  const popoverStates = usePopoverStates();
  let {relevantUrl} = useAuth();

  // Initialize filters with config defaults
  const filters = useStatisticsFilters(config.defaultValues);
  
  // Main state variables
  const [dateRange, setDateRange] = useState([subDays(today, config.defaultValues.dateRangeDays), today]);
  const [splitCount, setSplitCount] = useState(config.defaultValues.splitCount);
  const [viewMode, setViewMode] = useState(config.defaultValues.viewMode);
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // Operating hours state
  const [statsOpenHour, setStatsOpenHour] = useState(8);
  const [statsCloseHour, setStatsCloseHour] = useState(18);
  
  // Initial data fetch
  useEffect(() => {
    const initializeData = async () => {
      // Fetch initial data
      await data.fetchCartsData();
      await data.fetchUsersData();
      
      // Fetch and set operating hours
      const hours = await data.fetchSiteOperatingHours();
      if (hours) {
        setStatsOpenHour(hours.openHour);
        setStatsCloseHour(hours.closeHour);
      }
      
      // Fetch initial statistics and cart summary
      data.fetchStatisticsData(dateRange, splitCount, config.sectionVisibility, filters.getFilterStates(), statsOpenHour, statsCloseHour);
      data.fetchCartSummaryData(dateRange, filters.cartSummaryFilter);
    };
    
    initializeData();
  }, [relevantUrl]);

  // Keyboard shortcut for settings dialog
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === ',') {
        event.preventDefault();
        setSettingsOpen(true);
      }
      if (event.key === 'Escape' && settingsOpen) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [settingsOpen]);

  // Handlers
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const handleSplitCountChange = (newSplitCount) => {
    setSplitCount(newSplitCount);
  };
  
  const handleRefresh = () => {
    // When refreshing, fetch all data using the original method to ensure all sections are updated
    data.fetchStatisticsData(dateRange, splitCount, config.sectionVisibility, filters.getFilterStates(), statsOpenHour, statsCloseHour);
    data.fetchCartSummaryData(dateRange, filters.cartSummaryFilter);
  };

  const handleCartSummaryFilterChange = (newFilter) => {
    filters.setCartSummaryFilter(newFilter);
    data.fetchCartSummaryData(dateRange, newFilter);
  };

  const handleQuickRange = (days) => {
    const newEndDate = new Date();
    const newStartDate = subDays(newEndDate, days);
    setDateRange([newStartDate, newEndDate]);
  };

  // Filter change handlers that trigger data refresh for specific statistics sections
  const handleDemandFilterChange = () => {
    // Only fetch demand statistics when demand filters change
    data.fetchDemandStatistics(
      dateRange,
      splitCount,
      filters.getFilterStates(),
      statsOpenHour,
      statsCloseHour
    );
  };

  const handleCartFilterChange = (statisticsType) => {
    // Determine which cart-related statistics type is being filtered
    const type = statisticsType || 
      (popoverStates.availabilityFilterPopoverOpen ? 'availability' : 
       popoverStates.onServiceFilterPopoverOpen ? 'onService' : 
       popoverStates.waitingTimeFilterPopoverOpen ? 'waitingTime' : null);
    
    if (type) {
      // Only fetch the specific cart statistics type that was changed
      data.fetchCartStatistics(
        dateRange,
        splitCount,
        type,
        filters.getFilterStates(),
        statsOpenHour,
        statsCloseHour
      );
    } else {
      // Fallback to fetch all statistics if type cannot be determined
      console.warn('Could not determine which cart statistics type changed, fetching all');
      data.fetchStatisticsData(dateRange, splitCount, config.sectionVisibility, filters.getFilterStates(), statsOpenHour, statsCloseHour);
    }
  };

  const handleTripFilterChange = () => {
    // Only fetch trip statistics when trip filters change
    data.fetchTripStatistics(
      dateRange,
      splitCount,
      filters.getFilterStates(),
      statsOpenHour,
      statsCloseHour
    );
  };

  const handleStatsOpenHourChange = (newOpenHour) => {
    setStatsOpenHour(newOpenHour);
  };

  const handleStatsCloseHourChange = (newCloseHour) => {
    setStatsCloseHour(newCloseHour);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 9, mb: 4 }}>
      <Box 
        sx={{ 
          mb: 4, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
          borderRadius: 2,
          py: 3,
          color: 'white',
          boxShadow: 3
        }}
      >
        <Typography variant="h4" gutterBottom component="div" align="center" sx={{ fontWeight: 'bold' }}>
            Statistics Dashboard
          </Typography>
        <Typography variant="subtitle1" align="center">
          Analyze your data with interactive visualizations
        </Typography>
      </Box>

      {/* Floating Settings Button */}
      <Tooltip 
        title={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Time Period & Chart Settings
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Ctrl+, or Cmd+, to open
            </Typography>
          </Box>
        } 
        placement="left"
      >
        <Fab
          color="primary"
          aria-label="settings"
          onClick={() => setSettingsOpen(true)}
          sx={{
            position: 'fixed',
            top: 80,
            left: 140,
            zIndex: 1000,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2)'
            },
            transition: 'all 0.2s ease-in-out',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              borderRadius: '50%',
              zIndex: -1,
              opacity: 0.3,
              animation: 'pulse 2s infinite'
            },
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 0.3
              },
              '50%': {
                transform: 'scale(1.2)',
                opacity: 0.1
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 0.3
              }
            }
          }}
        >
          <SettingsIcon />
        </Fab>
      </Tooltip>

      {/* Settings Dialog */}
      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        splitCount={splitCount}
        onSplitCountChange={handleSplitCountChange}
        statsOpenHour={statsOpenHour}
        onStatsOpenHourChange={handleStatsOpenHourChange}
        statsCloseHour={statsCloseHour}
        onStatsCloseHourChange={handleStatsCloseHourChange}
        operatingHoursDisplay={data.operatingHoursDisplay}
        isLoading={data.isLoading}
        onRefresh={handleRefresh}
        getConfigOptionsWithFallback={config.getConfigOptionsWithFallback}
        getConfigValueWithDefault={config.getConfigValueWithDefault}
        isQuickSelectActive={(option) => config.isQuickSelectActive(option, dateRange)}
        onQuickRange={handleQuickRange}
      />

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ 
            opacity: data.isLoading ? 0.7 : 1,
            transition: 'opacity 0.3s'
          }}>
            {data.statisticsError ? (
              <Card sx={{ mb: 3, textAlign: 'center', py: 4 }}>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    📊 No Data Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Statistics data is currently unavailable for the selected time period and filters. 
                    Please try adjusting your date range or check back later.
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={handleRefresh}
                    startIcon={<RefreshIcon />}
                    color="primary"
                  >
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <SyncHighlight 
                statisticsData={data.statisticsData}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                tripType={filters.tripType}
                tripTypeOptions={tripTypeOptions}
                onDemandFilterIconClick={popoverStates.handleDemandFilterIconClick}
                onAvailabilityFilterIconClick={popoverStates.handleAvailabilityFilterIconClick}
                onOnServiceFilterIconClick={popoverStates.handleOnServiceFilterIconClick}
                onWaitingTimeFilterIconClick={popoverStates.handleWaitingTimeFilterIconClick}
                onTripFilterIconClick={popoverStates.handleTripFilterIconClick}
                cartSummaryComponent={config.sectionVisibility.cartSummary ? (
                  <CartSummaryStats
                    data={data.cartSummaryData}
                    isLoading={data.cartSummaryLoading}
                    cartsList={data.cartsList}
                    onCartFilterChange={handleCartSummaryFilterChange}
                    visibility={config.cartSummaryVisibility}
                    noWrapper={true}
                  />
                ) : null}
                demandFilterComponent={config.sectionVisibility.demand ? {} : null}
                availabilityFilterComponent={config.sectionVisibility.availability ? {} : null}
                onServiceFilterComponent={config.sectionVisibility.onService ? {} : null}
                waitingTimeFilterComponent={config.sectionVisibility.waitingTime ? {} : null}
                tripsFilterComponent={config.sectionVisibility.trips ? {} : null}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Demand Filter Popover */}
      <DemandFilterPopover
        open={popoverStates.demandFilterPopoverOpen}
        onClose={popoverStates.handleDemandFilterPopoverClose}
        demandFilterType={filters.demandFilterType}
        setDemandFilterType={filters.setDemandFilterType}
        selectedUserTypes={filters.selectedUserTypes}
        handleUserTypesChange={filters.handleUserTypesChange}
        userCreationPeriod={filters.userCreationPeriod}
        handleCreationPeriodChange={filters.handleCreationPeriodChange}
        selectedUsers={filters.selectedUsers}
        handleSpecificUsersChange={filters.handleSpecificUsersChange}
        usersList={data.usersList}
        userTypeOptions={userTypeOptions}
        creationPeriodOptions={creationPeriodOptions}
        onFilterChange={handleDemandFilterChange}
        demandYAxisPercentage={filters.demandYAxisPercentage}
        handleDemandYAxisPercentageChange={filters.handleDemandYAxisPercentageChange}
      />

      {/* Availability Filter Popover */}
      <CartFilterPopover
        open={popoverStates.availabilityFilterPopoverOpen}
        onClose={popoverStates.handleAvailabilityFilterPopoverClose}
        sectionName="availability"
        sectionTitle="Availability"
        cartFilterType={filters.availabilityCartFilterType}
        setCartFilterType={filters.setAvailabilityCartFilterType}
        selectedCartTypes={filters.selectedAvailabilityCartTypes}
        handleCartTypesChange={filters.handleAvailabilityCartTypesChange}
        selectedCarts={filters.selectedAvailabilityCarts}
        handleSpecificCartsChange={filters.handleAvailabilitySpecificCartsChange}
        cartsList={data.cartsList}
        cartTypeOptions={cartTypeOptions}
        onFilterChange={() => handleCartFilterChange('availability')}
        availabilityYAxisPercentage={filters.availabilityYAxisPercentage}
        handleAvailabilityYAxisPercentageChange={filters.handleAvailabilityYAxisPercentageChange}
      />

      {/* On Service Filter Popover */}
      <CartFilterPopover
        open={popoverStates.onServiceFilterPopoverOpen}
        onClose={popoverStates.handleOnServiceFilterPopoverClose}
        sectionName="onService"
        sectionTitle="On Service"
        cartFilterType={filters.onServiceCartFilterType}
        setCartFilterType={filters.setOnServiceCartFilterType}
        selectedCartTypes={filters.selectedOnServiceCartTypes}
        handleCartTypesChange={filters.handleOnServiceCartTypesChange}
        selectedCarts={filters.selectedOnServiceCarts}
        handleSpecificCartsChange={filters.handleOnServiceSpecificCartsChange}
        cartsList={data.cartsList}
        cartTypeOptions={cartTypeOptions}
        onFilterChange={() => handleCartFilterChange('onService')}
        onServiceYAxisPercentage={filters.onServiceYAxisPercentage}
        handleOnServiceYAxisPercentageChange={filters.handleOnServiceYAxisPercentageChange}
      />

      {/* Waiting Time Filter Popover */}
      <CartFilterPopover
        open={popoverStates.waitingTimeFilterPopoverOpen}
        onClose={popoverStates.handleWaitingTimeFilterPopoverClose}
        sectionName="waitingTime"
        sectionTitle="Waiting Time"
        cartFilterType={filters.waitingTimeCartFilterType}
        setCartFilterType={filters.setWaitingTimeCartFilterType}
        selectedCartTypes={filters.selectedWaitingTimeCartTypes}
        handleCartTypesChange={filters.handleWaitingTimeCartTypesChange}
        selectedCarts={filters.selectedWaitingTimeCarts}
        handleSpecificCartsChange={filters.handleWaitingTimeSpecificCartsChange}
        cartsList={data.cartsList}
        cartTypeOptions={cartTypeOptions}
        onFilterChange={() => handleCartFilterChange('waitingTime')}
        waitingTimeYAxisPercentage={filters.waitingTimeYAxisPercentage}
        handleWaitingTimeYAxisPercentageChange={filters.handleWaitingTimeYAxisPercentageChange}
      />

      {/* Trip Filter Popover */}
      <TripFilterPopover
        open={popoverStates.tripFilterPopoverOpen}
        onClose={popoverStates.handleTripFilterPopoverClose}
        cartFilterType={filters.tripCartFilterType}
        setCartFilterType={filters.setTripCartFilterType}
        selectedCartTypes={filters.selectedTripCartTypes}
        handleCartTypesChange={filters.handleTripCartTypesChange}
        selectedCarts={filters.selectedTripCarts}
        handleSpecificCartsChange={filters.handleTripSpecificCartsChange}
        cartsList={data.cartsList}
        cartTypeOptions={cartTypeOptions}
        selectedTripStatuses={filters.selectedTripStatuses}
        handleTripStatusesChange={filters.handleTripStatusesChange}
        tripType={filters.tripType}
        handleTripTypeChange={filters.handleTripTypeChange}
        tripStatusOptions={tripStatusOptions}
        tripTypeOptions={tripTypeOptions}
        onFilterChange={handleTripFilterChange}
        tripYAxisPercentage={filters.tripYAxisPercentage}
        handleTripYAxisPercentageChange={filters.handleTripYAxisPercentageChange}
      />
    </Container>
  );
};

export default Statistics_new;